<script setup lang="ts">
  import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
  import { useReportsStore } from '../../store';
  import ReportCard from './ReportCard.vue';
  import Pagination from '../shared/Pagination.vue';
  import ReportCardSkeleton from '../skeletons/ReportCardSkeleton.vue';
  import { useDisplay } from 'vuetify';
  import DataStateHandler from '../shared/DataStateHandler.vue';

  const display = useDisplay();
  const mdAndUp = computed(() => display.mdAndUp.value);

  const reportsStore = useReportsStore();
  const itemsPerPage = ref(reportsStore.itemsPerPage);
  const sortOption = ref(reportsStore.sortOption);
  const observer = ref<IntersectionObserver | null>(null);
  const isLoading = ref(false);

  // Main watcher to sync local isLoading with the store's loading state.
  watch(
    () => reportsStore.loading,
    (newStoreLoadingState) => {
      isLoading.value = newStoreLoadingState;
      if (!newStoreLoadingState && !reportsStore.error) {
        setTimeout(() => {
          // Check if sortedReports is initialized and has data
          if (
            sortedReports &&
            typeof sortedReports.value !== 'undefined' &&
            sortedReports.value.length > 0
          ) {
            requestAnimationFrame(() => {
              setupLazyLoading();
            });
          }
        }, 0);
      }
    },
    { immediate: true },
  );

  // Watch for store changes to update local reactive properties.
  watch(
    () => reportsStore.sortOption,
    (newSort) => {
      if (sortOption.value !== newSort) {
        sortOption.value = newSort;
      }
    },
  );

  watch(
    () => reportsStore.itemsPerPage,
    (newItemsPerPage) => {
      if (itemsPerPage.value !== newItemsPerPage)
        itemsPerPage.value = newItemsPerPage;
    },
  );

  // Sorts the reports from the store based on the local sortOption.
  const sortedReports = computed(() => {
    if (!reportsStore.reports || !reportsStore.reports.length) {
      return [];
    }
    const reports = [...reportsStore.reports];
    switch (sortOption.value) {
      case 'Recently Published':
        return reports.sort((a, b) => {
          const aDate = (a as any).release || '';
          const bDate = (b as any).release || '';
          return new Date(bDate).getTime() - new Date(aDate).getTime();
        });
      case 'Alphabetical':
        return reports.sort((a, b) => a.title.localeCompare(b.title));
      case 'Copyright':
        return reports.sort((a, b) => {
          const aYear = (a as any).copyright
            ? parseInt((a as any).copyright.match(/\d{4}/)?.[0] || '0')
            : 0;
          const bYear = (b as any).copyright
            ? parseInt((b as any).copyright.match(/\d{4}/)?.[0] || '0')
            : 0;
          return bYear - aYear;
        });
      default:
        return reports;
    }
  });

  const totalPages = computed(() => {
    if (reportsStore.totalItems === 0) return 0;
    return Math.ceil(reportsStore.totalItems / itemsPerPage.value);
  });

  // currentItems directly uses the sorted reports from the store.
  const currentItems = computed(() => {
    return sortedReports.value;
  });

  // Handles sort changes from the mobile sort dropdown.
  function handleSortChange(newSortValue: string): void {
    if (reportsStore.sortOption !== newSortValue) {
      reportsStore.setSortOption(newSortValue);
    }
  }

  // Handle pagination changes directly to the store
  async function handlePageChange(newPage: number): Promise<void> {
    if (reportsStore.currentPage !== newPage) {
      reportsStore.setPage(newPage);
    }
  }

  function setupLazyLoading(): void {
    if (typeof IntersectionObserver === 'undefined') return;

    if (observer.value) {
      observer.value.disconnect();
    }

    observer.value = new IntersectionObserver(
      (entries) => {
        if (entries.length > 5) {
          entries.slice(0, 5).forEach((entry) => {
            if (entry.isIntersecting) {
              const card = entry.target as HTMLElement;
              card.classList.add('visible');
              observer.value?.unobserve(card);
            }
          });

          if (entries.length > 5) {
            // Use requestIdleCallback with a fallback to setTimeout
            if (typeof requestIdleCallback === 'function') {
              requestIdleCallback(() => {
                entries.slice(5).forEach((entry) => {
                  if (entry.isIntersecting) {
                    const card = entry.target as HTMLElement;
                    card.classList.add('visible');
                    observer.value?.unobserve(card);
                  }
                });
              });
            } else {
              setTimeout(() => {
                entries.slice(5).forEach((entry) => {
                  if (entry.isIntersecting) {
                    const card = entry.target as HTMLElement;
                    card.classList.add('visible');
                    observer.value?.unobserve(card);
                  }
                });
              }, 1); // Minimal timeout as fallback
            }
          }
        } else {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const card = entry.target as HTMLElement;
              card.classList.add('visible');
              observer.value?.unobserve(card);
            }
          });
        }
      },
      {
        root: null,
        threshold: 0.1,
        rootMargin: '100px',
      },
    );

    requestAnimationFrame(() => {
      const cards = document.querySelectorAll('.report-card');
      cards.forEach((card) => {
        observer.value?.observe(card);
      });
    });
  }

  // Watcher for selected filters from the store.
  // Assumes store actions for filters (setSubject, etc.) handle fetching.
  watch(
    [
      () => reportsStore.selectedSubjects,
      () => reportsStore.selectedFormats,
      () => reportsStore.selectedGrades,
    ],
    () => {
      // This watcher can be used for side-effects in this component if needed when filters change.
      // Fetching is primarily managed by the store's filter actions.
    },
    { deep: true },
  );

  const errorMessage = ref('');
  const showRetryButton = ref(false);

  // Sync local error display with the store's error state.
  watch(
    () => reportsStore.error,
    (newError) => {
      if (newError) {
        errorMessage.value = newError;
        showRetryButton.value = true;
      } else {
        errorMessage.value = '';
        showRetryButton.value = false;
      }
    },
  );

  // Retries fetching reports by calling the store action.
  function retryLoadReports() {
    reportsStore.fetchReports();
  }

  onMounted(() => {
    // Sync local reactive properties with the store
    itemsPerPage.value = reportsStore.itemsPerPage;
    sortOption.value = reportsStore.sortOption;

    // The watcher for reportsStore.loading (with immediate: true) will handle
    // the display of skeleton loaders.
    // setupLazyLoading if reports are already present from the store.
    if (sortedReports.value.length > 0) {
      requestAnimationFrame(() => {
        setupLazyLoading();
      });
    }
  });

  onBeforeUnmount(() => {
    if (observer.value) {
      observer.value.disconnect();
      observer.value = null;
    }
  });
</script>

<template>
  <div v-show="!mdAndUp" :class="mdAndUp ? 'mb-4' : 'mb-8'">
    <div
      class="sort-by-container d-flex align-center"
      :class="mdAndUp ? 'justify-end' : 'justify-start'"
    >
      <h5 class="text-secondary font-weight-bold">Sort by:</h5>
      <v-select
        v-model="sortOption"
        :items="['Recently Published', 'Alphabetical', 'Copyright']"
        variant="outlined"
        :hide-details="true"
        density="compact"
        class="small-select pl-2"
        append-inner-icon="mdi-chevron-down"
        aria-label="Sort reports by criteria"
        :menu-props="{
          contentClass: 'select-menu-content',
          transition: 'scale-transition',
          location: 'bottom',
        }"
        @update:model-value="handleSortChange"
      />
    </div>
  </div>

  <DataStateHandler
    :loading="isLoading"
    :error="reportsStore.error || errorMessage"
    :isEmpty="
      sortedReports.length === 0 &&
      !isLoading &&
      !(reportsStore.error || errorMessage)
    "
    class="reports-grid-container"
  >
    <template #loading>
      <v-row no-gutters>
        <v-col
          v-for="n in itemsPerPage"
          :key="`skeleton-${n}`"
          cols="12"
          sm="6"
          lg="4"
          class="d-flex report-grid-col"
        >
          <ReportCardSkeleton />
        </v-col>
      </v-row>
    </template>

    <template #error="{ errorMessage: stateError }">
      <div class="no-results-message pa-4 text-center">
        <p class="text-grey mb-4">{{ stateError }}</p>
        <button
          v-if="showRetryButton"
          type="button"
          class="btn btn-outline-primary"
          @click="retryLoadReports"
          :class="{ disabled: isLoading }"
          :disabled="isLoading"
        >
          {{ isLoading ? 'Loading...' : 'Retry' }}
        </button>
      </div>
    </template>

    <template #empty>
      <div class="no-results-message pa-4 text-center">
        <p class="text-grey mb-4">
          {{
            reportsStore.error ||
            errorMessage ||
            'No reports found matching your criteria.'
          }}
        </p>
        <button
          v-if="showRetryButton"
          type="button"
          class="btn btn-outline-primary"
          @click="retryLoadReports"
          :class="{ disabled: isLoading }"
          :disabled="isLoading"
        >
          {{ isLoading ? 'Loading...' : 'Retry' }}
        </button>
      </div>
    </template>

    <v-row no-gutters>
      <v-col
        v-for="(report, index) in currentItems"
        :key="`${report.slug}-${index}`"
        cols="12"
        sm="6"
        lg="4"
        class="d-flex report-grid-col"
      >
        <ReportCard
          class="report-card flex-grow-1"
          :report="report"
          :instance-id="`${report.slug}-${index}`"
          fetchpriority="high"
        />
      </v-col>
    </v-row>
  </DataStateHandler>

  <Pagination
    v-if="!isLoading && sortedReports.length > 0 && totalPages > 1"
    :currentPage="reportsStore.currentPage"
    :totalPages="totalPages"
    @update:currentPage="handlePageChange"
  />
</template>

<style scoped>
  .report-card {
    width: 100%; /* Ensure card takes full width of v-col */
    /* height: 100%; Let flexbox handle height */
    display: flex;
    will-change: opacity;
  }

  .loading-info {
    color: var(--v-secondary-base, #333);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 10px;
  }

  :deep(.v-card) {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  :deep(.content-container) {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .no-results-message {
    grid-column: 1 / -1; /* Keep for potential non-grid fallback? Or remove? */
  }

  .reports-grid-container {
    min-height: 500px;
  }

  .report-grid-col {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  /* Remove right padding from rightmost cards to align with container edge */
  @media (min-width: 1264px) {
    .report-grid-col:nth-child(3n) {
      padding-right: 0 !important;
    }
  }

  @media (min-width: 600px) and (max-width: 1263px) {
    .report-grid-col:nth-child(2n) {
      padding-right: 0 !important;
    }
  }

  @media (max-width: 599px) {
    .report-grid-col {
      padding-right: 0 !important;
    }
  }
</style>
